import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Footer from '../components/layout/Footer';
import { HERO } from '../constants/images';
import { mdMessage, policies, certifications, orgStructureCards } from '../data/mock';
import { Check, Award, FileText, ChevronRight, ChevronLeft, X } from 'lucide-react';
import { ScrollReveal, StaggerContainer, StaggerItem } from '../components/ui/ScrollReveal';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from '../components/ui/carousel';

const viewableCertifications = certifications.filter((c) => !c.comingSoon && c.pdfUrl);

// Animation variants - matching home page HeroSection exactly
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 32 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
  },
};

const AboutPage = () => {
  const [carouselApi, setCarouselApi] = React.useState(null);
  const [pdfViewerOpen, setPdfViewerOpen] = React.useState(false);
  const [pdfViewerIndex, setPdfViewerIndex] = React.useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      // Use scroll attachment for mobile & tablet - fixed causes slow/janky scroll on touch devices
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  React.useEffect(() => {
    if (!carouselApi) return;
    // Slower auto-scroll on mobile for better performance
    const delay = isMobile ? 5000 : 3800;
    const interval = setInterval(() => {
      carouselApi.scrollNext();
    }, delay);
    return () => clearInterval(interval);
  }, [carouselApi, isMobile]);

  const openPdfViewer = React.useCallback((cert) => {
    const idx = viewableCertifications.findIndex((c) => c.id === cert.id);
    if (idx >= 0) {
      setPdfViewerIndex(idx);
      setPdfViewerOpen(true);
    }
  }, []);

  const goPrev = React.useCallback(() => {
    setPdfViewerIndex((i) => (i - 1 + viewableCertifications.length) % viewableCertifications.length);
  }, []);

  const goNext = React.useCallback(() => {
    setPdfViewerIndex((i) => (i + 1) % viewableCertifications.length);
  }, []);

  React.useEffect(() => {
    const onEscape = (e) => {
      if (e.key === 'Escape') setPdfViewerOpen(false);
    };
    if (pdfViewerOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', onEscape);
    }
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onEscape);
    };
  }, [pdfViewerOpen]);

  return (
    <div className="min-h-screen">
      <motion.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      >
        {/* Hero Section - large, content bottom left & right */}
        <section
          id="hero"
          className="relative min-h-[70vh] sm:min-h-[65vh] flex items-end"
          style={{
            backgroundImage: `url(${HERO.aboutHero})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: isMobile ? 'scroll' : 'fixed',
          }}
        >
          {/* Dark overlay */}
          <div className="absolute inset-0 bg-[#1A1A1A]/70" />
          <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 sm:pb-12 lg:pb-16 pt-20 sm:pt-24 md:pt-32">
            <motion.div
              key="hero-content"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 sm:gap-8 lg:gap-12"
            >
              <motion.h1
                variants={itemVariants}
                className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-white font-semibold leading-[1.1] tracking-tight"
              >
                Our story.
              </motion.h1>
              <motion.p
                variants={itemVariants}
                className="text-white/90 text-sm sm:text-base lg:text-lg max-w-md leading-relaxed text-left font-normal"
              >
                Specializing in erection, installation, commissioning, operation, maintenance, up-gradation, and renovation of industrial projects and power plants across India.
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* Mission, Vision, Core Values - no gap, solid dark */}
        <section id="vision-mission" className="bg-[#1A1A1A] py-12 sm:py-14 md:py-16 lg:py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <StaggerContainer staggerDelay={0.1} threshold={0.05}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-12 lg:gap-16">
                  <StaggerItem variant="fadeUp">
                    <h3 className="text-white font-semibold text-base sm:text-lg lg:text-xl mb-3">Our Vision</h3>
                    <div className="w-10 h-px bg-white/40 mb-4 sm:mb-6" />
                    <p className="text-white/75 text-sm sm:text-base leading-relaxed">
                      To become a leading and most trusted engineering service partner in India for thermal power, steel, and industrial plant projects by setting benchmarks in safety, quality workmanship, and execution excellence.
                    </p>
                  </StaggerItem>
                  <StaggerItem variant="fadeUp">
                    <h3 className="text-white font-semibold text-base sm:text-lg lg:text-xl mb-3">Our Mission</h3>
                    <div className="w-10 h-px bg-white/40 mb-4 sm:mb-6" />
                    <p className="text-white/75 text-sm sm:text-base leading-relaxed">
                      To deliver safe, timely, and high-quality mechanical erection, boiler repair, and maintenance services for core industries by using skilled manpower, strong technical expertise, and ethical business practices—ensuring maximum client satisfaction and operational reliability.
                    </p>
                  </StaggerItem>
                  <StaggerItem variant="fadeUp">
                    <h3 className="text-white font-semibold text-base sm:text-lg lg:text-xl mb-3">Core Value</h3>
                    <div className="w-10 h-px bg-white/40 mb-4 sm:mb-6" />
                    <p className="text-white/75 text-sm sm:text-base leading-relaxed">
                      To uphold safety, quality, integrity, timely delivery in every project ensuring customer satisfaction and meaningful impact to client and community.
                    </p>
                  </StaggerItem>
                </div>
              </StaggerContainer>
            </div>
          </section>

        {/* MD Message Section */}
        <section id="md-message" className="py-12 sm:py-16 md:py-20 lg:py-24 bg-[#FAF8F2]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollReveal variant="fadeUp" threshold={0.1}>
              <div className="grid grid-cols-1 lg:grid-cols-[0.42fr_0.58fr] gap-8 sm:gap-12 lg:gap-12 items-start">
                <StaggerContainer staggerDelay={0.08} threshold={0.05} className="relative w-full lg:mt-20">
                  <StaggerItem variant="fadeUp">
                    <img
                      src={mdMessage.image}
                      alt={mdMessage.name}
                      className="w-full aspect-[3/4] object-cover object-center"
                      loading="lazy"
                    />
                    <div className="absolute -bottom-4 -right-4 sm:-bottom-6 sm:-right-6 bg-[#8B7355] text-white p-4 sm:p-6">
                      <div className="text-2xl sm:text-3xl font-light mb-1">27+</div>
                      <div className="text-xs sm:text-sm">Years Leading</div>
                    </div>
                  </StaggerItem>
                </StaggerContainer>
                <StaggerContainer staggerDelay={0.08} threshold={0.05} className="flex flex-col">
                  <StaggerItem variant="fadeUp">
                    <span className="text-[#8B7355] text-xs sm:text-sm font-semibold tracking-widest uppercase">
                      Message from MD
                    </span>
                  </StaggerItem>
                  <StaggerItem variant="fadeUp">
                    <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl text-[#1A1A1A] mt-2 sm:mt-3 mb-6 sm:mb-8">
                      A word from our <span className="italic">leader</span>
                    </h2>
                  </StaggerItem>
                  <StaggerItem variant="fadeUp">
                    <div className="text-[#6B6B65] leading-relaxed text-base sm:text-lg space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                      {mdMessage.message.split('\n\n').map((paragraph, i) => (
                        <p key={i}>{paragraph}</p>
                      ))}
                    </div>
                  </StaggerItem>
                  <StaggerItem variant="fadeUp">
                    <p className="text-[#1A1A1A] font-semibold text-base sm:text-lg">{mdMessage.name}</p>
                    <p className="text-[#8B7355] text-xs sm:text-sm mt-1">{mdMessage.role}</p>
                  </StaggerItem>
                </StaggerContainer>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* Policies Section */}
        <ScrollReveal variant="fadeUp" threshold={0.05} amount={0.1}>
          <section id="policies" className="py-12 sm:py-16 md:py-20 lg:py-24 bg-[#FAF8F2]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="text-[#8B7355] text-xs sm:text-sm font-semibold tracking-widest uppercase">Our Standards</span>
            <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-[#1A1A1A] mt-2 sm:mt-3 mb-8 sm:mb-12">
              Company <span className="italic">policies</span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {policies.map((policy) => (
                <div key={policy.id} className="flex gap-3 sm:gap-4 p-5 sm:p-6 bg-[#F5F3F0] hover:bg-[#1A1A1A] group transition-all duration-300">
                  <span className="w-9 h-9 sm:w-10 sm:h-10 bg-[#8B7355]/10 group-hover:bg-[#8B7355] flex items-center justify-center flex-shrink-0 transition-colors">
                    <Check className="w-4 h-4 sm:w-5 sm:h-5 text-[#8B7355] group-hover:text-white transition-colors" />
                  </span>
                  <div>
                    <h3 className="font-serif text-base sm:text-lg text-[#1A1A1A] group-hover:text-white mb-2 transition-colors">
                      {policy.title}
                    </h3>
                    <p className="text-[#6B6B65] group-hover:text-white/70 text-sm leading-relaxed transition-colors">
                      {policy.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
        </ScrollReveal>

        {/* Our Organization - Organizational Structure */}
        <ScrollReveal variant="fadeUp" threshold={0.05} amount={0.1}>
          <section id="organization" className="py-12 sm:py-16 md:py-20 lg:py-24 bg-[#F5F3F0]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <span className="text-[#8B7355] text-xs sm:text-sm font-semibold tracking-widest uppercase">
                Our Organization
              </span>
              <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-[#1A1A1A] mt-2 sm:mt-3 mb-3 sm:mb-4">
                Organizational <span className="italic">Structure</span>
              </h2>
              <p className="text-[#6B6B65] text-base sm:text-lg max-w-2xl mb-10 sm:mb-14">
                A strong foundation built on expertise and leadership.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                {orgStructureCards.map((card) => (
                  <div
                    key={card.id}
                    className="group relative bg-white p-6 sm:p-8 rounded-lg border border-[#E0DDD9] hover:border-[#8B7355]/40 hover:shadow-xl hover:shadow-[#8B7355]/10 hover:-translate-y-1 transition-all duration-300 overflow-hidden"
                  >
                    <span className="absolute top-4 right-4 sm:top-6 sm:right-6 text-3xl sm:text-4xl font-light text-[#8B7355]/20 group-hover:text-[#8B7355]/40 transition-colors">
                      {String(card.id).padStart(2, '0')}
                    </span>
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#8B7355]/30 to-transparent group-hover:from-[#8B7355] transition-all" />
                    <span className="text-[#8B7355] text-xs font-semibold tracking-widest uppercase">
                      {card.division}
                    </span>
                    <h3 className="font-serif text-lg sm:text-xl text-[#1A1A1A] mt-2 mb-2 sm:mb-3 group-hover:text-[#8B7355] transition-colors">
                      {card.role}
                    </h3>
                    <p className="text-[#6B6B65] text-sm leading-relaxed">
                      {card.description}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-10 sm:mt-14 text-center">
                <Link
                  to="/team"
                  className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 text-sm font-semibold text-white bg-[#8B7355] hover:bg-[#7A6348] transition-colors"
                >
                  Meet the Team
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </section>
        </ScrollReveal>

        {/* Certifications Section – moving large cards, click opens PDF */}
        <ScrollReveal variant="fadeUp" threshold={0.05} amount={0.1}>
          <section id="certifications" className="py-12 sm:py-16 md:py-20 lg:py-24 bg-[#1A1A1A] overflow-hidden">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8 sm:mb-12">
              <span className="text-[#8B7355] text-xs sm:text-sm font-semibold tracking-widest uppercase">Our Credentials</span>
              <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white mt-2 sm:mt-3">
                Certifications & <span className="italic">accreditations</span>
              </h2>
              <p className="text-white/60 text-sm sm:text-base mt-3 sm:mt-4 max-w-xl">Click a card to view the certification. Use the arrows in the viewer to switch between certificates.</p>
            </div>

            <div className="relative px-2 sm:px-4 lg:px-12">
              <Carousel
                setApi={setCarouselApi}
                opts={{
                  align: 'start',
                  loop: true,
                  skipSnaps: false,
                  dragFree: false,
                  duration: 32,
                }}
                className="w-full relative"
              >
                <CarouselContent className="-ml-4 md:-ml-6">
                  {certifications.map((cert) => {
                    const isComingSoon = cert.comingSoon;
                      const cardContent = (
                      <div className={`h-full min-h-[260px] sm:min-h-[280px] md:min-h-[320px] rounded-xl border-2 p-5 sm:p-6 md:p-8 transition-all duration-300 flex flex-col ${isComingSoon ? 'border-white/10 bg-white/5 cursor-default' : 'border-white/10 bg-white/5 hover:border-[#8B7355] hover:bg-white/[0.08] hover:shadow-[0_0_40px_-8px_rgba(198,123,77,0.4)] hover:scale-[1.02] active:scale-[0.99]'}`}>
                        <div className="flex items-start justify-between mb-4 sm:mb-6">
                          <span className={`flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-xl transition-colors ${isComingSoon ? 'bg-white/10 text-white/60' : 'bg-[#8B7355]/20 text-[#8B7355] group-hover:bg-[#8B7355]/30'}`}>
                            <Award className="h-6 w-6 sm:h-7 sm:w-7" />
                          </span>
                          {isComingSoon ? (
                            <span className="rounded-full bg-white/10 px-2.5 sm:px-3 py-1 sm:py-1.5 text-[10px] sm:text-xs font-medium text-white/70">
                              Coming soon
                            </span>
                          ) : (
                            <span className="flex items-center gap-1.5 text-[10px] sm:text-xs font-medium text-[#8B7355] opacity-0 transition-opacity group-hover:opacity-100">
                              <FileText className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                              View PDF
                            </span>
                          )}
                        </div>
                        <h3 className="font-serif text-xl sm:text-2xl md:text-3xl text-white mb-2 sm:mb-3 transition-colors">
                          {cert.name}
                        </h3>
                        <p className="text-white/60 text-sm leading-relaxed flex-1">
                          {cert.description}
                        </p>
                        <div className="mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-white/10">
                          {isComingSoon ? (
                            <span className="inline-flex items-center gap-2 text-white/50 text-xs sm:text-sm font-medium">
                              PDF will be available soon
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-2 text-[#8B7355] text-xs sm:text-sm font-medium group-hover:translate-x-1 transition-transform">
                              Open certificate
                              <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 transition-transform group-hover:translate-x-1" />
                            </span>
                          )}
                        </div>
                      </div>
                    );
                    return (
                      <CarouselItem
                        key={cert.id}
                        className="pl-2 sm:pl-4 md:pl-6 basis-[90%] sm:basis-[85%] md:basis-[45%] lg:basis-[380px]"
                      >
                        {isComingSoon ? (
                          <div className="group block h-full pointer-events-none select-none" aria-label={`${cert.name} — Coming soon`}>
                            {cardContent}
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => openPdfViewer(cert)}
                            className="group block h-full w-full text-left"
                          >
                            {cardContent}
                          </button>
                        )}
                      </CarouselItem>
                    );
                  })}
                </CarouselContent>
                <CarouselPrevious className="left-0 md:-left-2 h-10 w-10 sm:h-12 sm:w-12 rounded-full border-white/20 bg-[#1A1A1A] text-white hover:bg-[#8B7355] hover:border-[#8B7355] hover:text-white disabled:opacity-30" />
                <CarouselNext className="right-0 md:-right-2 h-10 w-10 sm:h-12 sm:w-12 rounded-full border-white/20 bg-[#1A1A1A] text-white hover:bg-[#8B7355] hover:border-[#8B7355] hover:text-white disabled:opacity-30" />
              </Carousel>
            </div>
          </section>
        </ScrollReveal>
      </motion.main>
      <Footer />

      {/* In-page PDF viewer modal */}
      {pdfViewerOpen && viewableCertifications[pdfViewerIndex] && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Certificate viewer"
        >
          {/* Backdrop – click to close */}
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => setPdfViewerOpen(false)}
            aria-hidden="true"
          />
          {/* Panel – tall so full PDF page is visible */}
          <div
            className="relative flex w-full max-w-5xl flex-col rounded-2xl bg-[#1A1A1A] shadow-2xl transition-all duration-300 ease-out"
            style={{ height: '94vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex shrink-0 items-center justify-between border-b border-white/10 px-4 sm:px-6 py-3 sm:py-4">
              <h3 className="font-serif text-base sm:text-lg md:text-xl text-white pr-2">
                {viewableCertifications[pdfViewerIndex].name}
              </h3>
              <button
                type="button"
                onClick={() => setPdfViewerOpen(false)}
                className="rounded-lg p-2 text-white/70 transition-colors hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-[#8B7355] flex-shrink-0"
                aria-label="Close viewer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {/* PDF area – flex-1 so iframe fills remaining height */}
            <div className="relative flex min-h-0 flex-1 flex-col">
              {/* Left nav */}
              <button
                type="button"
                onClick={goPrev}
                className="absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-[#1A1A1A] p-2 sm:p-2.5 text-white shadow-lg transition-colors hover:bg-[#8B7355] focus:outline-none focus:ring-2 focus:ring-[#8B7355] md:left-4"
                aria-label="Previous certificate"
              >
                <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
              <iframe
                key={viewableCertifications[pdfViewerIndex].pdfUrl}
                src={`${viewableCertifications[pdfViewerIndex].pdfUrl}#view=Fit`}
                title={viewableCertifications[pdfViewerIndex].name}
                className="min-h-0 flex-1 w-full border-0 bg-white"
              />
              {/* Right nav */}
              <button
                type="button"
                onClick={goNext}
                className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-[#1A1A1A] p-2 sm:p-2.5 text-white shadow-lg transition-colors hover:bg-[#8B7355] focus:outline-none focus:ring-2 focus:ring-[#8B7355] md:right-4"
                aria-label="Next certificate"
              >
                <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
            </div>
            {/* Footer – counter */}
            <div className="flex shrink-0 items-center justify-center gap-2 border-t border-white/10 px-4 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm text-white/60">
              <span>
                {pdfViewerIndex + 1} / {viewableCertifications.length}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AboutPage;
